import React, { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import TimeslotParametersBlock from "./TimeslotParametersBlock";
import TimeslotStructureBlock from "./TimeslotStructureBlock";
import FastAPI from "../../../api/FastAPI";
import { FillingType } from "../../../api/FastAPI.types";
import useFastTimeslotMutation from "../../../hooks/mutations/useFASTTimeslotMutations";
import useFASTLayout from "../../../hooks/useFASTLayout";
import prettyDurations from "../../../utils/durations";
import NameHeaderInput from "../../form/NameHeaderInput";
import SaveButton from "../../form/SaveButton";

import type { JSX } from "react";
import type { SubmitErrorHandler } from "react-hook-form";

import type { TimeslotRule } from "../../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

export interface TimeslotFormValues {
  name: string;
  color: string;
  durationHours: {
    value: number;
    label: string;
  } | null;
  durationMinutes: {
    value: number;
    label: string;
  } | null;
  rulesById: Record<string, TimeslotRule>;
  fillingType: { value: FillingType; label?: string };
  stuffingList: {
    value: string | null;
    label?: string;
  } | null;
  interstitialAdbreak: {
    value: string | null;
    label?: string;
  } | null;
}

const FORM_ID = "timeslot-edit-form";

export default function TimeslotEdit(): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const { channelId, timeslotId } = useParams<
    FASTChannelRoutesParams & {
      timeslotId: string;
    }
  >();

  const isNewTimeslot = timeslotId === "new";

  const getTimeslotQuery = FastAPI.generateQuery({
    fn: FastAPI.getTimeslot,
    params: [{ channelId, timeslotId }],
  });
  const { data: timeslotData, isFetching } = useQuery({
    ...getTimeslotQuery,
    enabled: !isNewTimeslot,
  });

  const getPlaylistListQuery = FastAPI.generateQuery({
    fn: FastAPI.getPlaylistList,
    params: [channelId],
  });
  const { data: playlistListData, isLoading: isLoadingPlaylistList } =
    useQuery(getPlaylistListQuery);

  const getAdbreakListQuery = FastAPI.generateQuery({
    fn: FastAPI.getAdBreaksList,
    params: [channelId],
  });

  const { data: getAdbreakListData, isLoading: isLoadingAdbreakList } =
    useQuery(getAdbreakListQuery);

  const interstitialAdbreakOptions = useMemo(
    () =>
      getAdbreakListData?.data?.map((adbreak) => ({
        value: adbreak.uuid,
        label: adbreak.name,
      })) ?? [],
    [getAdbreakListData],
  );

  const stuffingListOptions = useMemo(
    () =>
      playlistListData?.data?.map((playlist) => ({
        value: playlist.uuid,
        label: playlist.name,
      })) ?? [],
    [playlistListData],
  );

  const { mod } = prettyDurations(timeslotData?.duration_ms ?? 0, {
    valueUnit: "ms",
  });

  const rulesById = useMemo(
    () =>
      timeslotData?.rules.reduce((acc: Record<string, TimeslotRule>, rule) => {
        acc[rule.uuid] = rule;
        return acc;
      }, {}) ?? {},
    [timeslotData],
  );

  const methods = useForm<TimeslotFormValues>({
    values: {
      name: isNewTimeslot ? t("newTimeslot") : (timeslotData?.name ?? ""),
      color: timeslotData?.color ?? "#FFFFFF",
      durationHours: {
        value: mod.hours,
        label: `${mod.hours}h`,
      },
      durationMinutes: {
        value: mod.minutes,
        label: `${mod.minutes}m`,
      },
      rulesById,
      fillingType: { value: timeslotData?.type ?? FillingType.SingleRun },
      stuffingList: { value: timeslotData?.filling_playlist_uuid ?? null },
      interstitialAdbreak: {
        value: timeslotData?.interstitial_adbreak_uuid ?? null,
      },
    },
  });

  useFASTLayout({
    title: <NameHeaderInput control={methods.control} />,
    button: <SaveButton formId={FORM_ID} />,
    breadcrumbs: [
      {
        name: t("timeslots"),
        url: `/fast-channels/${channelId}/timeslots`,
      },
      {
        name: isNewTimeslot ? t("newTimeslot") : (timeslotData?.name ?? ""),
      },
    ],
  });

  const { updateFASTTimeslot } = useFastTimeslotMutation();

  const handleFormSubmit = useCallback(
    (formData: TimeslotFormValues) => {
      const durationMs =
        ((formData.durationHours?.value ?? 0) * 60 +
          (formData.durationMinutes?.value ?? 0)) *
        60 *
        1000;

      const ruleList = Object.values(formData.rulesById);

      if (durationMs === 0) {
        methods.setError("durationHours", {
          type: "manual",
          message: t("durationIsRequired"),
        });
        return;
      }

      if (ruleList.length === 0) {
        toast.error(t("oneRuleIsRequired"));
        return;
      }

      updateFASTTimeslot.mutate({
        isNewTimeslot,
        updatedTimeslot: {
          ...timeslotData,
          color: formData.color,
          duration_ms: durationMs,
          name: formData.name,
          rules: ruleList,
          type: formData.fillingType.value,
          filling_playlist_uuid: formData.stuffingList?.value ?? null,
          interstitial_adbreak_uuid:
            formData.interstitialAdbreak?.value ?? null,
        },
      });
    },
    [updateFASTTimeslot, isNewTimeslot, timeslotData, methods, t],
  );

  const handleInvalidForm: SubmitErrorHandler<TimeslotFormValues> = useCallback(
    (formErrors) => {
      const rulesError = formErrors.rulesById;

      if (rulesError) {
        const ruleIdErrorList = Object.keys(rulesError);

        const errorRuleNumbers = ruleIdErrorList
          .map((ruleId) => {
            const rule = methods.getValues(`rulesById.${ruleId}`);

            return rule.rank + 1;
          })
          .reduce(
            (acc, val, index) => (index > 0 ? `${acc}, ${val}` : acc + val),
            "",
          );

        toast.error(
          t("updateRuleError", {
            errorRuleNumber: errorRuleNumbers,
            count: ruleIdErrorList.length,
          }),
        );
      }
    },
    [methods, t],
  );

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <form
        className="flex w-full flex-col gap-[0.78125rem]"
        id={FORM_ID}
        onSubmit={methods.handleSubmit(handleFormSubmit, handleInvalidForm)}
      >
        <TimeslotParametersBlock
          interstitialAdbreakOptions={interstitialAdbreakOptions}
          isLoadingAdbreakList={isLoadingAdbreakList}
          isLoadingStuffingList={isLoadingPlaylistList}
          stuffingListOptions={stuffingListOptions}
        />
        <TimeslotStructureBlock isLoading={isFetching} />
      </form>
    </FormProvider>
  );
}
